import { useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ChartTheme, SiteOccupancyDatum } from '../types';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';

// Beyond 6 rows, height scales at this rate so an expanded list (e.g. after
// clicking "Overige") keeps the same per-row space instead of cramming more
// bars into a fixed height.
const ROW_HEIGHT = 32;

interface SiteOccupancyChartProps {
  data: SiteOccupancyDatum[];
  ariaLabel: string;
  chartTheme: ChartTheme;
  onSelect: (siteId: string) => void;
}

export default function SiteOccupancyChart({
  data,
  ariaLabel,
  chartTheme,
  onSelect,
}: SiteOccupancyChartProps) {
  const [activeIdx, setActiveIdx] = useState<number | undefined>();
  const kbActive = useRef(false);
  const { chartTextColor, gridColor, tooltipStyle } = chartTheme;

  return (
    <div
      tabIndex={0}
      role="group"
      aria-label={ariaLabel}
      className="outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
      onFocus={(e) => {
        if (e.target === e.currentTarget) {
          kbActive.current = true;
          setActiveIdx(0);
        }
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          kbActive.current = false;
          setActiveIdx(undefined);
        }
      }}
      onKeyDown={(e) => {
        const len = data.length;
        if (!len) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIdx((prev) => ((prev ?? -1) + 1) % len);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIdx((prev) => ((prev ?? 0) - 1 + len) % len);
        } else if ((e.key === 'Enter' || e.key === ' ') && activeIdx !== undefined) {
          e.preventDefault();
          onSelect(data[activeIdx].id);
        }
      }}
    >
      <ResponsiveContainer width="100%" height={data.length > 6 ? Math.max(320, data.length * ROW_HEIGHT) : 240}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: chartTextColor, fontSize: 13 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: chartTextColor, fontSize: 13 }}
            axisLine={false}
            tickLine={false}
            width={130}
            tickFormatter={(v: string) => (v.length > 16 ? v.slice(0, 15) + '…' : v)}
          />
          <ChartTooltip
            content={({ payload }) => {
              if (!payload?.length) return null;
              return (
                <div style={{ ...tooltipStyle, padding: '0.375rem 0.75rem' }}>
                  {payload[0].value} {LABELS.machines.toLowerCase()}
                </div>
              );
            }}
          />
          <Bar
            dataKey="machines"
            radius={[0, 4, 4, 0]}
            maxBarSize={22}
            cursor="pointer"
            onClick={(entry: { id: string }) => onSelect(entry.id)}
          >
            {data.map((entry, idx) => (
              <Cell
                key={idx}
                fill={entry.color}
                fillOpacity={activeIdx !== undefined && idx !== activeIdx ? 0.35 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
